import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useEventContext } from 'providers/EventProvider';
import { useSplitContext } from 'providers/SplitProvider';
import {
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Typography
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { SIDEBAR_WIDTH } from './layout';

const PINK = '#ff0060';
const SELECTED_BG = '#ffc1e0';

const convertTime = (time: number) => {
  const min = Math.floor(time / 60);
  const sec = Math.round(time % 60);
  return `${min}:${sec < 10 ? '0' : ''}${sec}`;
};

const getSplitLabel = (index: number, total: number) => {
  if (index === 0 && index === total - 1) return 'S-F';
  if (index === 0) return `S-${index + 1}`;
  if (index === total - 1) return `${index}-F`;
  return `${index}-${index + 1}`;
};

type CompetitorSplitsPanelProps = {
  sidebarOpen?: boolean;
  isDesktop?: boolean;
};

const CompetitorSplitsPanel: React.FC<CompetitorSplitsPanelProps> = ({
  sidebarOpen = false,
  isDesktop = true
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const competitorFromUrl = searchParams.get('competitor');
  const { players, updatePlayers } = useEventContext();
  const { splits, activeSplits, setActiveSplits, followedPlayerId, setFollowedPlayerId, time } = useSplitContext();
  const [expanded, setExpanded] = React.useState(() => competitorFromUrl !== null);
  const [selectedPlayerId, setSelectedPlayerId] = React.useState<number | ''>(() => {
    if (!competitorFromUrl) return '';
    const id = Number(competitorFromUrl);
    return Number.isNaN(id) ? '' : id;
  });
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const splitItemRefs = React.useRef<(HTMLDivElement | null)[]>([]);

  const playersWithSplits = React.useMemo(
    () => players.filter((player) => player.splits?.length > 0),
    [players]
  );

  React.useEffect(() => {
    if (playersWithSplits.length === 0) return;

    const followedHasSplits =
      followedPlayerId !== null && playersWithSplits.some((player) => player.id === followedPlayerId);

    if (followedHasSplits) {
      setSelectedPlayerId(followedPlayerId);
      if (competitorFromUrl !== String(followedPlayerId)) {
        setSearchParams(
          (params) => {
            params.set('competitor', String(followedPlayerId));
            return params;
          },
          { replace: true }
        );
      }
      return;
    }

    if (competitorFromUrl) {
      const id = Number(competitorFromUrl);
      if (!Number.isNaN(id) && playersWithSplits.some((player) => player.id === id)) {
        setSelectedPlayerId(id);
        return;
      }
    }

    setSelectedPlayerId((current) => {
      if (current !== '' && playersWithSplits.some((player) => player.id === current)) {
        return current;
      }
      return playersWithSplits[0].id;
    });
  }, [playersWithSplits, competitorFromUrl, followedPlayerId, setSearchParams]);

  const selectedPlayer = playersWithSplits.find((player) => player.id === selectedPlayerId);

  const bestTimes = React.useMemo(() => {
    return splits.map((_, index) => {
      const times = playersWithSplits
        .map((player) => player.splits[index]?.time ?? 0)
        .filter((time) => time > 0);
      return times.length > 0 ? Math.min(...times) : 0;
    });
  }, [splits, playersWithSplits]);

  const handlePlayerChange = (event: SelectChangeEvent<number | ''>) => {
    const id = event.target.value as number;
    setSelectedPlayerId(id);
    setFollowedPlayerId(id);
    updatePlayers((currentPlayers) =>
      currentPlayers.map((player) => ({
        ...player,
        options: { ...player.options, highlight: player.id === id }
      }))
    );
    setSearchParams(
      (params) => {
        params.set('competitor', String(id));
        return params;
      },
      { replace: true }
    );
  };

  const handleSplitClick = (index: number) => {
    setActiveSplits([index]);
  };

  const isSplitActive = (index: number) => activeSplits.includes(index);

  const currentSplitIndex = React.useMemo(() => {
    if (activeSplits.length === 0 || !selectedPlayer) return null;
    if (time <= 0) return activeSplits.length === 1 ? activeSplits[0] : null;

    let elapsedMs = 0;
    for (const splitIndex of activeSplits) {
      elapsedMs += (selectedPlayer.splits[splitIndex]?.time ?? 0) * 1000;
      if (time < elapsedMs) return splitIndex;
    }

    return activeSplits[activeSplits.length - 1] ?? null;
  }, [activeSplits, selectedPlayer, time]);

  const isCurrentSplit = (index: number) =>
    currentSplitIndex !== null ? index === currentSplitIndex : isSplitActive(index);

  React.useEffect(() => {
    if (!expanded || activeSplits.length === 0) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const isVisible = (element: HTMLElement) => {
      const rect = element.getBoundingClientRect();
      return rect.left >= containerRect.left && rect.right <= containerRect.right;
    };

    const indicesToShow = currentSplitIndex !== null ? [currentSplitIndex] : activeSplits;
    for (const index of indicesToShow) {
      const element = splitItemRefs.current[index];
      if (element && !isVisible(element)) {
        element.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
        break;
      }
    }
  }, [activeSplits, currentSplitIndex, expanded]);

  const showPanel = playersWithSplits.length > 0 && splits.length > 0;
  const compact = !isDesktop;

  if (!showPanel) {
    return null;
  }

  const panelPositionSx = {
    right: sidebarOpen ? SIDEBAR_WIDTH : 0
  };

  const iconButtonSx = compact
    ? { minWidth: 36, minHeight: 36, p: 0.5, flexShrink: 0 }
    : { minWidth: 44, minHeight: 44 };

  const expandButton = (
    <IconButton
      color="primary"
      size="small"
      onClick={() => setExpanded(true)}
      aria-label="expand"
      sx={{
        position: 'absolute',
        bottom: 12,
        left: 8,
        zIndex: 500,
        background: '#ffffffaa',
        ...iconButtonSx
      }}
    >
      <KeyboardArrowUpIcon fontSize={compact ? 'small' : 'medium'} />
    </IconButton>
  );

  const renderPlayerOption = (player: { id: number; name: string; color: string }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 0.5 : 1, minWidth: 0, overflow: 'hidden' }}>
      <Box
        sx={{
          width: compact ? 8 : 10,
          height: compact ? 8 : 10,
          borderRadius: '50%',
          backgroundColor: player.color,
          flexShrink: 0
        }}
      />
      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {player.name}
      </Box>
    </Box>
  );

  const playerSelect = (
    <FormControl size="small" sx={{ minWidth: 0, flex: 1 }}>
      <Select
        value={selectedPlayerId}
        onChange={handlePlayerChange}
        displayEmpty
        renderValue={(value) => {
          const player = playersWithSplits.find((item) => item.id === value);
          return player ? renderPlayerOption(player) : null;
        }}
        sx={{
          fontSize: compact ? '0.75rem' : '0.875rem',
          '& .MuiSelect-select': compact ? { py: 0.5, px: 1, display: 'flex', alignItems: 'center' } : undefined
        }}
      >
        {playersWithSplits.map((player) => (
          <MenuItem key={player.id} value={player.id} sx={{ fontSize: compact ? '0.75rem' : undefined }}>
            {renderPlayerOption(player)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const collapseButton = (
    <IconButton
      size="small"
      onClick={() => setExpanded(false)}
      aria-label="collapse"
      sx={iconButtonSx}
    >
      <KeyboardArrowDownIcon fontSize={compact ? 'small' : 'medium'} />
    </IconButton>
  );

  const splitCards =
    selectedPlayer && (
      <Box
        ref={scrollContainerRef}
        sx={{
          display: 'flex',
          gap: compact ? 0.5 : 1,
          px: compact ? 0.75 : 1.5,
          py: compact ? 0.5 : 1,
          overflowX: 'auto',
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': { height: compact ? 4 : 6 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: 3 }
        }}
      >
        {splits.map((_, index) => {
          const split = selectedPlayer.splits[index];
          const splitTime = split?.time ?? 0;
          const bestTime = bestTimes[index];
          const diff = splitTime > 0 && bestTime > 0 ? splitTime - bestTime : 0;
          const isBest = splitTime > 0 && diff === 0;

          const isCurrent = isCurrentSplit(index);

          return (
            <Paper
              key={index}
              ref={(element) => {
                splitItemRefs.current[index] = element;
              }}
              elevation={0}
              onClick={() => handleSplitClick(index)}
              sx={{
                minWidth: compact ? 56 : 88,
                flexShrink: 0,
                px: compact ? 0.5 : 1,
                py: compact ? 0.25 : 0.75,
                border: isCurrent ? `1.5px solid ${PINK}` : '1px solid rgba(0, 0, 0, 0.12)',
                backgroundColor: isCurrent ? SELECTED_BG : '#fff',
                textAlign: 'center',
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  fontWeight: isCurrent ? 'bold' : 'normal',
                  fontSize: compact ? '0.65rem' : undefined,
                  lineHeight: 1.2
                }}
              >
                {getSplitLabel(index, splits.length)}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 'bold',
                  color: selectedPlayer.color,
                  lineHeight: 1.15,
                  fontSize: compact ? '0.75rem' : undefined
                }}
              >
                {splitTime > 0 ? convertTime(splitTime) : '—'}
              </Typography>
              {isBest ? (
                <Typography
                  variant="caption"
                  sx={{ color: '#2e7d32', fontWeight: 'bold', fontSize: compact ? '0.6rem' : undefined, lineHeight: 1.2 }}
                >
                  best
                </Typography>
              ) : diff > 0 ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: compact ? '0.6rem' : undefined, lineHeight: 1.2 }}
                >
                  +{convertTime(diff)}
                </Typography>
              ) : (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: compact ? '0.6rem' : undefined, lineHeight: 1.2 }}
                >
                  —
                </Typography>
              )}
            </Paper>
          );
        })}
      </Box>
    );

  if (!expanded) {
    return expandButton;
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        zIndex: 500,
        padding: compact ? '2px 0 0' : '5px',
        backgroundColor: 'rgba(255, 255, 255, 0.97)',
        borderTop: '1px solid rgba(0, 0, 0, 0.12)',
        boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
        ...panelPositionSx
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: compact ? 0.5 : 1,
          px: compact ? 0.5 : 1.5,
          py: compact ? 0 : 0.5,
          minWidth: 0,
          borderBottom: compact ? 'none' : '1px solid rgba(0, 0, 0, 0.1)'
        }}
      >
        {collapseButton}
        {playerSelect}
        {isDesktop && (
          <Typography variant="subtitle2" sx={{ color: PINK, fontWeight: 'bold', whiteSpace: 'nowrap' }}>
            Competitor Splits
          </Typography>
        )}
      </Box>
      {splitCards}
    </Box>
  );
};

export default CompetitorSplitsPanel;
